import React, { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Link,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  OpenInNew,
  CalendarToday,
  AccessTime,
  Warning,
} from '@mui/icons-material';
import SEOHead from '../../components/SEO/SEOHead';
import PageHeader from '../../components/Layout/PageHeader';
import AtomicAnswer from '../../components/SEO/AtomicAnswer';
import FAQSection from '../../components/SEO/FAQSection';
import RelatedArticles from '../../components/Blog/RelatedArticles';
import NewsletterSection from '../../components/Layout/NewsletterSection';
import TrustStrip from '../../components/Layout/TrustStrip';
import { useTranslation } from 'react-i18next';

const RRSPDeadlinePage = () => {
  const { t, i18n } = useTranslation();
  const { lng } = useParams();
  const [showChecklist, setShowChecklist] = useState(false);

  const faqs = [
    { question: t('blog.rrspDeadline.faq1Q'), answer: t('blog.rrspDeadline.faq1A') },
    { question: t('blog.rrspDeadline.faq2Q'), answer: t('blog.rrspDeadline.faq2A') },
    { question: t('blog.rrspDeadline.faq3Q'), answer: t('blog.rrspDeadline.faq3A') },
    { question: t('blog.rrspDeadline.faq4Q'), answer: t('blog.rrspDeadline.faq4A') },
    { question: t('blog.rrspDeadline.faq5Q'), answer: t('blog.rrspDeadline.faq5A') },
  ];

  return (
    <Box component="article" sx={{ bgcolor: '#F7F9FC', minHeight: '100vh' }}>
      <SEOHead
        title={t('blog.rrspDeadline.seoTitle')}
        description={t('blog.rrspDeadline.seoDescription')}
        canonical="/blog/rrsp-deadline-marzo-2026"
        routeKey="blogRRSP"
        seoKey="blogRRSP"
        noIndex={i18n.language !== 'es'}
        ogImage="https://www.finmaple.com/images/blog/rrsp-deadline-ahorro-canada.jpg"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: t('blog.rrspDeadline.seoTitle'),
          description: t('blog.rrspDeadline.seoDescription'),
          datePublished: '2026-02-22',
          dateModified: '2026-03-09',
          author: { '@type': 'Organization', name: 'FinMaple', url: 'https://www.finmaple.com' },
          publisher: {
            '@type': 'Organization',
            name: 'FinMaple',
            url: 'https://www.finmaple.com',
            logo: { '@type': 'ImageObject', url: 'https://www.finmaple.com/og-image.jpg' },
          },
          mainEntityOfPage: 'https://www.finmaple.com/blog/rrsp-deadline-marzo-2026',
          speakable: {
            '@type': 'SpeakableSpecification',
            cssSelector: ['.article-headline', '.atomic-answer'],
          },
          inLanguage: i18n.language,
        }}
      />

      <PageHeader
        variant="functional"
        title={t('blog.rrspDeadline.pageTitle')}
        subtitle={t('blog.rrspDeadline.pageSubtitle')}
        breadcrumbs={[{ label: t('blog.rrspDeadline.breadcrumbGuides'), to: `/${lng}/blog` }, { label: 'Quebec', to: `/${lng}/quebec` }, { label: t('blog.rrspDeadline.breadcrumbCurrent') }]}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2.5, mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#5F6B7A' }}>
            <CalendarToday sx={{ fontSize: 14 }} />
            <Typography variant="body2">{t('blog.rrspDeadline.date')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#5F6B7A' }}>
            <AccessTime sx={{ fontSize: 14 }} />
            <Typography variant="body2">{t('blog.rrspDeadline.readTime')}</Typography>
          </Box>
        </Box>
      </PageHeader>

      <TrustStrip />

      {/* Deadline passed banner */}
      {new Date() > new Date('2026-03-02T23:59:59') && (
        <Container maxWidth="md" sx={{ mt: 3 }}>
          <Box
            sx={{
              bgcolor: '#FFF3E0',
              border: '1px solid #F9A825',
              borderRadius: '12px',
              p: 3,
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Warning sx={{ color: '#E65100', fontSize: 28, flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#E65100', mb: 0.5 }}>
                {t('blog.rrspDeadline.deadlinePassed', 'This deadline has passed (March 2, 2026)')}
              </Typography>
              <Typography variant="body2" sx={{ color: '#5F6B7A', lineHeight: 1.6 }}>
                {t('blog.rrspDeadline.deadlinePassedDesc', 'The RRSP contribution deadline for the 2025 tax year was March 2, 2026. The information below is kept for reference. See the fiscal calendar for upcoming deadlines.')}
              </Typography>
            </Box>
            <Button
              component={RouterLink}
              to={`/${lng}/calendario-fiscal-2026`}
              variant="contained"
              sx={{
                bgcolor: '#1E3A5F',
                color: '#fff',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '14px',
                px: 3,
                flexShrink: 0,
                '&:hover': { bgcolor: '#1A3152' },
              }}
            >
              {t('blog.rrspDeadline.seeCalendar', 'Ver calendario fiscal')}
            </Button>
          </Box>
        </Container>
      )}

      <Container maxWidth="md" sx={{ pb: 4 }}>
        <AtomicAnswer text={t('blog.rrspDeadline.atomicAnswer')} />

        {/* Checklist Section */}
        <Box
          sx={{
            bgcolor: '#F7F9FC',
            borderLeft: '4px solid #1E3A5F',
            borderRadius: '0 10px 10px 0',
            p: 3,
            my: 4,
          }}
        >
          <Typography variant="h4" sx={{ color: '#1A1A1A', mb: 1 }}>
            {t('blog.rrspDeadline.checklistTitle')}
          </Typography>
          <Button
            onClick={() => setShowChecklist(!showChecklist)}
            endIcon={showChecklist ? <ExpandLess /> : <ExpandMore />}
            sx={{ color: '#1E3A5F', textTransform: 'none', fontWeight: 600, mb: 1 }}
          >
            {showChecklist ? t('blog.rrspDeadline.hideChecklist') : t('blog.rrspDeadline.showChecklist')}
          </Button>
          <Box sx={{ display: showChecklist ? 'flex' : 'none', flexDirection: 'column', gap: 2, mt: 1 }}>
              {[
                { num: 1, text: t('blog.rrspDeadline.step1') },
                { num: 2, text: t('blog.rrspDeadline.step2') },
                { num: 3, text: t('blog.rrspDeadline.step3') },
                { num: 4, text: t('blog.rrspDeadline.step4') },
                { num: 5, text: t('blog.rrspDeadline.step5') },
              ].map((step) => (
                <Box key={step.num} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: '#1E3A5F',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      fontSize: 14,
                      flexShrink: 0,
                      mt: 0.2,
                    }}
                  >
                    {step.num}
                  </Box>
                  <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.6 }}>
                    {step.text}
                  </Typography>
                </Box>
              ))}
            </Box>
        </Box>

        {/* Por qué importa */}
        <Typography variant="h4" sx={{ color: '#1A1A1A', mt: 5, mb: 3 }}>
          {t('blog.rrspDeadline.section1Title')}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          {t('blog.rrspDeadline.section1P1_before')}<strong>{t('blog.rrspDeadline.section1P1_bold')}</strong>{t('blog.rrspDeadline.section1P1_mid')}<RouterLink to={`/${lng}/blog/tfsa-vs-rrsp-vs-fhsa-inmigrantes`} style={{ color: '#1E3A5F', textDecoration: 'underline' }}>{t('blog.rrspDeadline.section1P1_link')}</RouterLink>{t('blog.rrspDeadline.section1P1_after')}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          {t('blog.rrspDeadline.section1P2_before')}<strong>{t('blog.rrspDeadline.section1P2_bold')}</strong>{t('blog.rrspDeadline.section1P2_after')}
        </Typography>

        {/* Cuánto contribuir */}
        <Typography variant="h4" sx={{ color: '#1A1A1A', mt: 5, mb: 3 }}>
          {t('blog.rrspDeadline.section2Title')}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          {t('blog.rrspDeadline.section2P1_before')}<strong>{t('blog.rrspDeadline.section2P1_bold1')}</strong>{t('blog.rrspDeadline.section2P1_mid')}<strong>{t('blog.rrspDeadline.section2P1_bold2')}</strong>{t('blog.rrspDeadline.section2P1_after')}
        </Typography>
        <Box
          sx={{
            bgcolor: '#FFFBF0',
            borderLeft: '4px solid #F9A825',
            borderRadius: '0 10px 10px 0',
            p: 3,
            my: 3,
          }}
        >
          <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.7 }}>
            <strong>{t('blog.rrspDeadline.exampleTitle')}</strong> {t('blog.rrspDeadline.exampleText')}
          </Typography>
        </Box>

        {/* Dónde abrir */}
        <Typography variant="h4" sx={{ color: '#1A1A1A', mt: 5, mb: 3 }}>
          {t('blog.rrspDeadline.section3Title')}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          {t('blog.rrspDeadline.section3P1')}
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1, color: '#1A1A1A', lineHeight: 1.7 } }}>
          <li><strong>Wealthsimple</strong> - {t('blog.rrspDeadline.option1')}</li>
          <li><strong>Questrade</strong> - {t('blog.rrspDeadline.option2')}</li>
          <li><strong>{t('blog.rrspDeadline.option3Label')}</strong> - {t('blog.rrspDeadline.option3')}</li>
          <li><strong>EQ Bank</strong> - {t('blog.rrspDeadline.option4')}</li>
          <li><strong>FHSA</strong> - {t('blog.rrspDeadline.option5_before')}<RouterLink to={`/${lng}/blog/primera-hipoteca-canada-guia`} style={{ color: '#1E3A5F', textDecoration: 'underline' }}>{t('blog.rrspDeadline.option5_link')}</RouterLink></li>
        </Box>

        {/* Quebec doble declaración */}
        <Box
          sx={{
            bgcolor: '#D32F2F',
            borderRadius: '10px',
            p: 3,
            mt: 4,
            display: 'flex',
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600, mb: 0.5 }}>
              {t('blog.rrspDeadline.quebecTitle')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
              {t('blog.rrspDeadline.quebecText_before')}<RouterLink to={`/${lng}/blog/guia-impuestos-latinos-quebec-2026`} style={{ color: '#fff', textDecoration: 'underline' }}>{t('blog.rrspDeadline.quebecText_link')}</RouterLink>{t('blog.rrspDeadline.quebecText_after')}
            </Typography>
          </Box>
          <Button
            component={RouterLink}
            to={`/${lng}/quebec`}
            variant="contained"
            sx={{
              bgcolor: '#fff',
              color: '#D32F2F',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '14px',
              px: 3,
              flexShrink: 0,
              '&:hover': { bgcolor: '#FFEBEE' },
            }}
          >
            {t('blog.rrspDeadline.quebecButton')}
          </Button>
        </Box>


        <RelatedArticles slug="rrsp-deadline-marzo-2026" />

        {/* FAQs */}
        <FAQSection faqs={faqs} title={t('blog.rrspDeadline.faqTitle')} />

        {/* Official Sources */}
        <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid #E6E9EE' }}>
          <Typography variant="subtitle2" sx={{ color: '#5F6B7A', fontWeight: 600, mb: 1.5 }}>
            {t('blog.rrspDeadline.sourcesTitle')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Link href="https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/rrsps-related-plans.html" target="_blank" rel="noopener noreferrer" sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {t('blog.rrspDeadline.sourceLink1')} <OpenInNew sx={{ fontSize: 14 }} />
            </Link>
            <Link href="https://www.canada.ca/en/revenue-agency/services/e-services/digital-services-individuals/account-individuals.html" target="_blank" rel="noopener noreferrer" sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {t('blog.rrspDeadline.sourceLink2')} <OpenInNew sx={{ fontSize: 14 }} />
            </Link>
          </Box>
          <Typography variant="caption" sx={{ color: '#5F6B7A', display: 'block', mt: 2 }}>
            {t('blog.rrspDeadline.updated')}
          </Typography>
        </Box>
      </Container>

      <NewsletterSection />
    </Box>
  );
};

export default RRSPDeadlinePage;
